/**
 * Product Types - Story 6.2: Create My Products Page
 * Comprehensive type definitions for product management
 */

/**
 * Product Status Enum
 * Represents the status of a product in the marketplace
 */
export enum ProductStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
}

/**
 * Product Category Type
 * Available product categories in the marketplace
 */
export type ProductCategory =
  | 'BIOGAS'
  | 'BIOMASS'
  | 'SEEDS'
  | 'EQUIPMENT'
  | 'FERTILIZER'
  | 'OTHER';

/**
 * Product Image Type
 * Represents product images with URL and alt text
 */
export interface ProductImage {
  id: string;
  url: string;
  altText: string;
  isPrimary: boolean;
}

/**
 * Product Data Type
 * Complete product information from API
 * Note: Backend sends 'productName' instead of 'name'
 */
export interface Product {
  id: string;
  sellerId?: string;
  name?: string; // Frontend field
  productName?: string; // Backend field
  description: string;
  category: ProductCategory;
  price: number;
  unit: string;
  status: ProductStatus;
  images: ProductImage[] | string[]; // Can be array of objects or strings
  inquiryCount?: number;
  totalInquiries?: number;
  newInquiries?: number;
  minimumOrderQuantity?: number; // Minimum order quantity constraint
  totalQuantity?: number; // Total quantity available for this product
  availableQuantity?: number; // Available quantity (not reserved/sold)
  createdAt: string;
  updatedAt: string;
  lastModified?: string;
}

/**
 * Product Row Type
 * Flattened product data for table display
 */
export interface ProductRow {
  id: string;
  image?: string;
  name: string;
  category: ProductCategory;
  status: ProductStatus;
  inquiries: number;
  createdDate: string;
  price?: number;
  unit?: string;
  totalQuantity?: number; // Total quantity for this product
  availableQuantity?: number; // Available quantity (not reserved/sold)
}

/**
 * Product Filter Options
 * Filters for product listing
 */
export interface ProductFilters {
  status: 'ALL' | ProductStatus;
  search: string;
  category?: ProductCategory;
  sortBy?: 'createdDate' | 'inquiryCount' | 'name';
  sortOrder?: 'asc' | 'desc';
  page: number;
  pageSize: number;
}

/**
 * Product Table Column Type
 * Represents a column in the products table
 */
export interface ProductTableColumn {
  id: string;
  label: string;
  sortable: boolean;
  visible: boolean;
  width?: string;
}

/**
 * Product Delete Request
 * Data structure for delete action confirmation
 */
export interface ProductDeleteRequest {
  productId: string;
  productName: string;
}

/**
 * Product Edit Action
 * Navigation data for edit action
 */
export interface ProductEditAction {
  productId: string;
  action: 'edit' | 'view' | 'delete';
  productName?: string;
}

/**
 * Product View Inquiries Action
 * Navigation data for viewing inquiries
 */
export interface ProductViewInquiriesAction {
  productId: string;
  productName: string;
  inquiryCount: number;
}

/**
 * Paginated Products Response
 * API response structure for paginated products
 */
export interface PaginatedProductsResponse {
  data: Product[];
  pagination: {
    page: number;
    pageSize: number;
    totalItems: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

/**
 * API Error Response Type
 */
export interface ApiErrorResponse {
  message: string;
  statusCode?: number;
  errorCode?: string;
}

/**
 * UseProducts Hook Return Type
 * Return value of useProducts hook
 */
export interface UseProductsReturn {
  products: ProductRow[];
  isLoading: boolean;
  isError: boolean;
  error: ApiErrorResponse | null;
  totalItems: number;
  totalPages: number;
  currentPage: number;
  refetch: () => Promise<void>;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

/**
 * UseProductFilters Hook Return Type
 * Return value of useProductFilters hook
 */
export interface UseProductFiltersReturn {
  filters: ProductFilters;
  setFilters: (filters: Partial<ProductFilters>) => void;
  resetFilters: () => void;
  updateStatus: (status: 'ALL' | ProductStatus) => void;
  updateSearch: (search: string) => void;
  updateSort: (sortBy: 'createdDate' | 'inquiryCount' | 'name', sortOrder: 'asc' | 'desc') => void;
  goToPage: (page: number) => void;
}

/**
 * Products Table Props
 * Props for ProductsTable component
 */
export interface ProductsTableProps {
  products: ProductRow[];
  isLoading: boolean;
  isError: boolean;
  error?: ApiErrorResponse | null;
  totalPages: number;
  currentPage: number;
  onViewClick?: (action: ProductEditAction) => void;
  onEditClick?: (action: ProductEditAction) => void;
  onViewInquiriesClick?: (action: ProductViewInquiriesAction) => void;
  onDeleteClick?: (action: ProductDeleteRequest) => void;
  onStatusChange?: (productId: string, newStatus: ProductStatus) => void;
  onStatusError?: (productId: string, error: ApiErrorResponse) => void;
  onPageChange?: (page: number) => void;
  onRowClick?: (productId: string) => void;
  onRetry?: () => void;
  sortBy?: 'createdDate' | 'inquiryCount' | 'name';
  onSortChange?: (sortBy: string, sortOrder: 'asc' | 'desc') => void;
}

/**
 * Product Status Badge Props
 * Props for ProductStatusBadge component
 */
export interface ProductStatusBadgeProps {
  status: ProductStatus;
  className?: string;
}

/**
 * MyProductsPage Props
 * Props for MyProductsPage component
 */
export interface MyProductsPageProps {
  title?: string;
  onNavigateToEdit?: (productId: string) => void;
  onNavigateToDetail?: (productId: string) => void;
  onNavigateToInquiries?: (productId: string) => void;
  /** When true, renders without SellerDashboardLayout wrapper (for embedding in other layouts) */
  disableLayout?: boolean;
}

/**
 * Product Action Dialog Props
 * Props for action confirmation dialogs
 */
export interface ProductActionDialogProps {
  isOpen: boolean;
  action: 'edit' | 'view' | 'delete';
  productId: string;
  productName: string;
  onConfirm: () => void;
  onCancel: () => void;
  isLoading?: boolean;
}

/**
 * Product Table Cell Renderer Type
 * Custom cell rendering functions
 */
export type ProductTableCellRenderer = (
  product: ProductRow,
  column: ProductTableColumn
) => React.ReactNode;

/**
 * Product Sort Type
 * Sort configuration for products
 */
export interface ProductSort {
  field: 'createdDate' | 'inquiryCount' | 'name';
  order: 'asc' | 'desc';
}

/**
 * Product Status Toggle Props
 * Props for ProductStatusToggle component (Story 6.6)
 */
export interface ProductStatusToggleProps {
  productId: string;
  productName: string;
  currentStatus: ProductStatus;
  onStatusChange?: (productId: string, newStatus: ProductStatus) => void;
  onError?: (error: ApiErrorResponse) => void;
  isLoading?: boolean;
  disabled?: boolean;
  className?: string;
}

/**
 * Product Status Toggle Action
 * Action payload for status toggle
 */
export interface ProductStatusToggleAction {
  productId: string;
  productName: string;
  oldStatus: ProductStatus;
  newStatus: ProductStatus;
  timestamp: string;
}
